import classNames from 'classnames';
import { type JSX, useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import ModalStep from '@alltrails/modal/components/Modal/ModalStep';
import type { RadioOptions } from '../../types/RadioOptions';
import DescriptiveRadioButton from '../DescriptiveRadioButton/DescriptiveRadioButton';
import styles from './DescriptiveRadioModal.module.scss';

export type DescriptiveRadioModalProps = {
  title: string;
  description?: string | JSX.Element;
  closeModal: () => void;
  closeOnAccept?: boolean;
  goBack?: () => void;
  radioOptions: RadioOptions[];
  selected?: string;
  acceptCallback: (param: string) => void;
  isSave?: boolean;
  collaborativeListSubtext?: boolean;
  hasCustomStyles?: boolean;
  hideCloseButton?: boolean;
};

const DescriptiveRadioModal = ({
  title,
  description,
  closeModal,
  closeOnAccept = false,
  goBack,
  radioOptions = [],
  selected = '',
  isSave,
  acceptCallback,
  collaborativeListSubtext,
  hasCustomStyles,
  hideCloseButton
}: DescriptiveRadioModalProps) => {
  const [selectedOption, setSelectedOption] = useState(selected);
  const radioGroupName = useId();

  const unMountModal = () => {
    goBack && !closeOnAccept ? goBack() : closeModal();
  };

  const handleAccept = () => {
    acceptCallback(selectedOption);
    unMountModal();
  };

  return (
    <ModalStep
      subtitle={description}
      hideCloseButton={hideCloseButton}
      onCloseRequest={() => {
        closeModal();
      }}
      title={title}
      buttons={{
        primaryButton: {
          text: isSave ? <FormattedMessage defaultMessage="Save" /> : <FormattedMessage defaultMessage="Apply" />,
          onClick: handleAccept
        },
        flatButton: {
          text: <FormattedMessage defaultMessage="Cancel" />,
          onClick: unMountModal
        }
      }}
      shouldHideHeader={hasCustomStyles}
      contentsClassName={classNames({ [styles.noPadding]: hasCustomStyles })}
      footerClassName={classNames({ [styles.footerPadding]: hasCustomStyles })}
    >
      <fieldset className={styles.radioGroup} aria-label={title}>
        {radioOptions.map((ro, idx) => (
          <DescriptiveRadioButton
            key={ro.name}
            autoFocus={selectedOption === ro.value || (idx === 0 && !selectedOption)}
            borderBottom={radioOptions.length - 1 > idx}
            name={radioGroupName}
            onChange={(value: string) => setSelectedOption(value)}
            selected={selectedOption === ro.value}
            subtext={ro.subtext}
            title={ro.title}
            value={ro.value}
            icon={ro.icon}
          />
        ))}
      </fieldset>
      {collaborativeListSubtext && (
        <div className={styles.collabListContainer}>
          <Typography component="div" variant="text100bold">
            <FormattedMessage defaultMessage="Collaborative lists" />
          </Typography>
          <Typography component="div" variant="text100" color="secondary">
            <FormattedMessage defaultMessage="These are lists that you have been added to by others or your personal lists that you've added others to. Collaborative list privacy is set by the list creator and can be changed by any collaborators within the list itself." />
          </Typography>
        </div>
      )}
    </ModalStep>
  );
};

export default DescriptiveRadioModal;
